<template>
  <b-container class="container-customer-form" v-if="form.form && formReady">
    <PForm :data="form.form" :default-values="defaultValues" :lang="form.lang" :loading="isSubmitting" @send="sendResult" @skip="skipForm"/>
  </b-container>
</template>

<script setup>
import {useRoute} from 'vue-router'
import {watch, onMounted, ref} from 'vue'
import {useFormStore} from './store'
import PForm from "../pform/PForm.vue";
import {fieldTypes} from "@/domain/pform/fields";
import {languages} from "@/domain/utils/languages";
import {translate} from "@/domain/utils/translations";

// Update page title when form is loaded
const updatePageTitle = (formData, lang = 'it') => {
  if (formData?.title) {
    // Use translate function to handle language fallbacks correctly
    document.title = translate(formData.title, lang) || 'GetPica'
  } else {
    document.title = 'GetPica'
  }
}

const form = useFormStore()
const route = useRoute()
const formReady = ref(false)
const defaultValues = ref({})
const isSubmitting = ref(false)

const decodeBase64 = (encoded, parseJson = true) => {
  try {
    // Fix URL-safe base64 encoding  
    const cleanBase64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = atob(cleanBase64)
    return parseJson ? JSON.parse(decoded) : decoded
  } catch (error) {
    console.error('Error decoding base64:', error)
    return null
  }
}

const initializeForm = async () => {
  const picaBackUriParam = route.query['pica-back-uri']
  const dataParam = route.query.data

  if (!route.params.id) {
    console.error('Missing form ID in URL')
    return
  }

  // The ID from URL is the form_id, but we need to use pica_service_id from external data
  // First decode external data if available (dataParam already declared above)
  let picaServiceId = route.params.id // Default to URL param if no external data
  
  if (dataParam) {
    const data = decodeBase64(dataParam)
    if (data?.pica_service_id) {
      picaServiceId = data.pica_service_id
    }
  }
  
  // Fetch form configuration using pica_service_id
  await form.fetchForm(picaServiceId)

  // Prepare initial data from external parameters
  let initialFormData = {}
  
  // Handle external parameters if present
  if (picaBackUriParam && dataParam) {
    try {
      // Decode parameters - both are URL-safe base64 encoded
      const backUri = decodeBase64(picaBackUriParam, false)
      const data = decodeBase64(dataParam, true)

      if (data) {
        // Save back URI in store
        form.setBackUri(backUri)

        // Extract data
        const { user_id, user_email, pica_service_id, lang } = data

        // Set language if provided and valid
        if (lang) {
          const validLangCodes = languages.map(l => l.code)
          if (validLangCodes.includes(lang)) {
            form.setLang(lang)
          } else {
            console.warn(`Invalid language code: ${lang}. Using default.`)
          }
        }

        // Pre-populate email field if exists and user_email is provided
        if (user_email && form.data?.fields) {
          const emailField = form.data.fields.find(field => field.type === fieldTypes.EMAIL)
          if (emailField) {
            initialFormData[emailField.key] = user_email
          }
        }

        // Store additional data if needed
        form.setExternalData({
          user_id,
          pica_service_id
        })
      }
    } catch (error) {
      console.error('Error decoding URL parameters:', error)
    }
  }

  // Set default values and mark form as ready
  defaultValues.value = initialFormData
  formReady.value = true
  
  // Update page title
  updatePageTitle(form.data, form.lang)
}

// Watch for route changes
watch(() => [route.params.id, route.query],
    async () => {
      await initializeForm()
    },
    {immediate: false})

onMounted(() => {
  initializeForm()
})

const sendResult = async (result) => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  try {
    await form.postResponse(result)
    await form.redirectAfterComplete()
  } finally {
    isSubmitting.value = false
  }
}

const skipForm = () => {
  form.redirectAfterComplete()
}

</script>